import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Link } from './PublishedLink';
import { SOCIAL_LINKS } from '../constants/socialLinks';
import SocialIcon from './SocialIcon';
import { FOOTER_GROUPS } from '../constants/navigation';


const Footer: React.FC = () => {
  const { t } = useTranslation();
  const startYear = 2024;
  const currentYear = new Date().getFullYear();
  const yearDisplay = currentYear > startYear ? `${startYear}-${currentYear}` : startYear;
  const location = useLocation();

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="mb-12">
          <Link to="/" langHint={false} className="inline-block mb-4">
            <img src="/logo.png" alt={t('header.logoAlt')} className="h-12 w-auto" />
          </Link>
          <p className="text-gray-300 max-w-md mb-6">
            {t('footer.tagline')}
          </p>
          <div className="flex space-x-4">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors transform hover:scale-110 duration-300"
                aria-label={social.name}
              >
                <SocialIcon social={social} className="w-6 h-6" />
              </a>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {FOOTER_GROUPS.map((group) => (
            <div key={group.headingKey}>
              <h3 className="text-lg font-bold mb-6 relative inline-block">
                {t(group.headingKey)}
                <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary"></span>
              </h3>
              <nav>
                <ul className="space-y-3">
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className={`text-gray-300 hover:text-white transition-colors ${
                          location.pathname === item.to ? 'text-primary' : ''
                        }`}
                      >
                        {t(item.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          ))}
        </div>
        
        <div className="border-t border-gray-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col items-center md:items-start">
              <p className="text-xs text-gray-400 mb-2">
                {t('footer.affiliateNotice')}
              </p>
              <p className="text-sm text-gray-400 mb-4 md:mb-0">
                {t('footer.copyright', { year: yearDisplay })}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 
